/**
 * PreviewPanel — Result preview display for the AnimationPreview editor.
 *
 * Handles: frame preview rendering, onion skin, createShiftedSprite,
 * bakeFrame, bakeAndSave, bake prompt, crosshair, and exit logic.
 */

import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { createTechButton, drawTechRect, fillTechRect, TECH } from '@/ui/TechUI';
import { SoundManager } from '@/systems/SoundManager';
import type { AnimPreviewState } from './AnimPreviewState';
import type { AnimFrameDef, AnimDef } from '../AnimationPreviewScene';
import type { SourcePanel } from './SourcePanel';
import {
  SIDEBAR_W, WORK_W, FRAME_X, FRAME_Y, FRAME_SIZE, FRAME_W,
  PREVIEW_SIZE, PREVIEW_X, PREVIEW_Y,
} from './layoutConstants';

/**
 * Callbacks the PreviewPanel needs from the host scene.
 */
export interface PreviewPanelCallbacks {
  autoSave(): void;
  refreshTimeline(): void;
  refreshFrameProps(): void;
  refreshSheetProps(): void;
  rebuildSidebarList(): void;
}

export class PreviewPanel {
  private scene: Phaser.Scene;
  private state: AnimPreviewState;
  private cb: PreviewPanelCallbacks;
  private sourcePanel: SourcePanel;

  // Preview
  previewContainer!: Phaser.GameObjects.Container;
  private previewSprite: Phaser.GameObjects.Sprite | null = null;
  frameCountLabel!: Phaser.GameObjects.Text;
  sheetInfoLabel!: Phaser.GameObjects.Text;

  // Onion skin
  private onionPrev: Phaser.GameObjects.Sprite | null = null;
  private onionNext: Phaser.GameObjects.Sprite | null = null;

  // Crosshair
  private crosshairGfx: Phaser.GameObjects.Graphics | null = null;

  // Bake prompt
  private bakePromptContainer: Phaser.GameObjects.Container | null = null;

  constructor(
    scene: Phaser.Scene,
    state: AnimPreviewState,
    cb: PreviewPanelCallbacks,
    sourcePanel: SourcePanel,
  ) {
    this.scene = scene;
    this.state = state;
    this.cb = cb;
    this.sourcePanel = sourcePanel;
  }

  /** Build the preview area (call once during create) */
  buildPanel(): void {
    const scene = this.scene;

    this.previewContainer = scene.add.container(FRAME_X, FRAME_Y);

    // Checkerboard background
    const checker = scene.add.graphics();
    const checkerSize = FRAME_SIZE + 40;
    const sq = 10;
    for (let cy = 0; cy < checkerSize; cy += sq) {
      for (let cx = 0; cx < checkerSize; cx += sq) {
        const dark = (Math.floor(cx / sq) + Math.floor(cy / sq)) % 2 === 0;
        checker.fillStyle(dark ? 0x222233 : 0x2a2a3d, 1);
        checker.fillRect(cx - checkerSize / 2, cy - checkerSize / 2, sq, sq);
      }
    }
    this.previewContainer.add(checker);

    const frameHalf = FRAME_SIZE / 2;
    const border = scene.add.graphics();
    drawTechRect(border, -frameHalf, -frameHalf, FRAME_SIZE, FRAME_SIZE, 0x334455, 0.4);
    this.previewContainer.add(border);

    scene.add.text(FRAME_X, FRAME_Y - frameHalf - 12, 'RESULT', {
      fontSize: '9px', fontFamily: 'monospace', fontStyle: 'bold', color: '#666688',
    }).setOrigin(0.5);

    // Sheet name + frame info
    this.sheetInfoLabel = scene.add.text(SIDEBAR_W + WORK_W / 2, 20, '', {
      fontSize: '13px', fontFamily: 'monospace', fontStyle: 'bold', color: '#ffcc00',
    }).setOrigin(0.5);

    this.frameCountLabel = scene.add.text(FRAME_X, FRAME_Y + frameHalf + 6, '', {
      fontSize: '10px', fontFamily: 'monospace', color: '#888899',
    }).setOrigin(0.5);
  }

  // ═══════════════════════════════════════════════════════
  // ── Frame display ──
  // ═══════════════════════════════════════════════════════

  /** Get the frame preview scale factor (frame -> RESULT panel) */
  getPreviewScale(): number {
    const maxSize = FRAME_SIZE - 20;
    const fw = this.state.animDef.frameWidth;
    const fh = this.state.animDef.frameHeight;
    return Math.min(maxSize / fw, maxSize / fh, 3);
  }

  /**
   * Create a sprite showing a shifted region of the source image.
   */
  createShiftedSprite(
    sheetIdx: number, offsetX: number, offsetY: number, fs: number,
  ): Phaser.GameObjects.Sprite {
    const scene = this.scene;
    const st = this.state;
    const tex = scene.textures.get(st.selectedSheet);
    const frame = tex.get(sheetIdx);
    const source = frame.source.image as HTMLImageElement;
    const cd = frame.canvasData as { x: number; y: number; width: number; height: number };

    const canvas = document.createElement('canvas');
    canvas.width = cd.width;
    canvas.height = cd.height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(source,
      cd.x - offsetX, cd.y - offsetY, cd.width, cd.height,
      0, 0, cd.width, cd.height,
    );

    const dynKey = `__shifted_${sheetIdx}_${offsetX}_${offsetY}`;
    if (scene.textures.exists(dynKey)) scene.textures.remove(dynKey);
    scene.textures.addCanvas(dynKey, canvas);

    const sprite = scene.add.sprite(0, 0, dynKey);
    const maxSize = PREVIEW_SIZE - 20;
    const baseScale = Math.min(maxSize / cd.width, maxSize / cd.height, 3);
    sprite.setScale(baseScale * fs);
    return sprite;
  }

  showFrame(idx: number): void {
    // Clean up previous sprites
    if (this.onionPrev) { this.onionPrev.destroy(); this.onionPrev = null; }
    if (this.onionNext) { this.onionNext.destroy(); this.onionNext = null; }
    if (this.previewSprite) { this.previewSprite.destroy(); this.previewSprite = null; }
    const st = this.state;
    if (!st.selectedSheet || st.frameCount === 0) return;

    const fi = Phaser.Math.Clamp(idx, 0, st.frameCount - 1);
    const maxSize = PREVIEW_SIZE - 20;
    const scene = this.scene;

    const getScale = (sheetIdx: number) => {
      const tex = scene.textures.get(st.selectedSheet);
      const f = tex?.get(sheetIdx);
      if (!f) return 1;
      return Math.min(maxSize / f.width, maxSize / f.height, 3);
    };

    const makeSprite = (frameOrderIdx: number): Phaser.GameObjects.Sprite => {
      const si = st.animDef.frameOrder[frameOrderIdx] ?? frameOrderIdx;
      const def = st.animDef.frames[frameOrderIdx];
      const ox = def?.offsetX ?? 0;
      const oy = def?.offsetY ?? 0;
      const fs = def?.scale ?? 1;
      const hasBbox = def?.bboxW != null && def.bboxW > 0;
      const hasAdjustments = hasBbox || ox !== 0 || oy !== 0 || fs !== 1;

      if (hasAdjustments) {
        const bakedCanvas = this.bakeFrame(si, def ?? { duration: 100, offsetX: 0, offsetY: 0, scale: 1 });
        const bk = hasBbox ? `${def!.bboxX}_${def!.bboxY}_${def!.bboxW}_${def!.bboxH}` : `${ox}_${oy}_${fs}`;
        const dynKey = `__preview_${si}_${bk}`;
        if (scene.textures.exists(dynKey)) scene.textures.remove(dynKey);
        scene.textures.addCanvas(dynKey, bakedCanvas);
        const spr = scene.add.sprite(0, 0, dynKey);
        const baseScale = getScale(si);
        spr.setScale(baseScale);
        return spr;
      } else {
        const spr = scene.add.sprite(0, 0, st.selectedSheet, si);
        spr.setScale(getScale(si));
        return spr;
      }
    };

    // Onion skin: previous frame (red)
    if (st.onionEnabled && fi > 0) {
      const prevSprite = makeSprite(fi - 1);
      prevSprite.setTint(0xff4444);
      prevSprite.setAlpha(0.3);
      this.previewContainer.add(prevSprite);
      this.onionPrev = prevSprite;
    }

    // Onion skin: next frame (blue)
    if (st.onionEnabled && fi < st.frameCount - 1) {
      const nextSprite = makeSprite(fi + 1);
      nextSprite.setTint(0x4488ff);
      nextSprite.setAlpha(0.3);
      this.previewContainer.add(nextSprite);
      this.onionNext = nextSprite;
    }

    // Current frame (on top)
    const sprite = makeSprite(fi);
    this.previewContainer.add(sprite);
    this.previewSprite = sprite;

    // Refresh sheet view overlay
    this.sourcePanel.refreshSheetView();

    // Draw bbox overlay if frame has one (skip if same frame and drag in progress)
    if (this.sourcePanel.bboxFrameIdx !== fi || !this.sourcePanel.isDragging) {
      this.sourcePanel.drawBboxOverlay(fi);
    }
  }

  /** Only re-render the preview sprite (no bbox rebuild) */
  refreshPreviewSprite(frameOrderIdx: number): void {
    if (this.previewSprite) { this.previewSprite.destroy(); this.previewSprite = null; }
    const st = this.state;
    if (!st.selectedSheet || st.frameCount === 0) return;
    const scene = this.scene;
    const fi = Phaser.Math.Clamp(frameOrderIdx, 0, st.frameCount - 1);
    const si = st.animDef.frameOrder[fi] ?? fi;
    const def = st.animDef.frames[fi];
    const hasDef = def && (def.bboxW != null && def.bboxW > 0 ||
      (def.offsetX ?? 0) !== 0 || (def.offsetY ?? 0) !== 0 || (def.scale ?? 1) !== 1);
    if (hasDef) {
      const bakedCanvas = this.bakeFrame(si, def);
      const dynKey = `__preview_live_${fi}`;
      if (scene.textures.exists(dynKey)) scene.textures.remove(dynKey);
      scene.textures.addCanvas(dynKey, bakedCanvas);
      const spr = scene.add.sprite(0, 0, dynKey);
      spr.setScale(this.getPreviewScale());
      this.previewContainer.add(spr);
      this.previewSprite = spr;
    } else {
      const spr = scene.add.sprite(0, 0, st.selectedSheet, si);
      spr.setScale(this.getPreviewScale());
      this.previewContainer.add(spr);
      this.previewSprite = spr;
    }
  }

  // ═══════════════════════════════════════════════════════
  // ── Crosshair ──
  // ═══════════════════════════════════════════════════════

  drawCrosshair(): void {
    if (this.crosshairGfx) { this.crosshairGfx.destroy(); this.crosshairGfx = null; }
    const st = this.state;
    if (!st.crosshairEnabled) return;

    const half = PREVIEW_SIZE / 2;
    const gfx = this.scene.add.graphics();
    gfx.lineStyle(1, 0x44ffaa, 0.4);
    gfx.lineBetween(PREVIEW_X, PREVIEW_Y - half, PREVIEW_X, PREVIEW_Y + half);
    gfx.lineBetween(PREVIEW_X - half, PREVIEW_Y, PREVIEW_X + half, PREVIEW_Y);
    gfx.lineStyle(1, 0x44ffaa, 0.8);
    gfx.lineBetween(PREVIEW_X - 8, PREVIEW_Y, PREVIEW_X + 8, PREVIEW_Y);
    gfx.lineBetween(PREVIEW_X, PREVIEW_Y - 8, PREVIEW_X, PREVIEW_Y + 8);
    gfx.setDepth(10);
    this.crosshairGfx = gfx;
  }

  // ═══════════════════════════════════════════════════════
  // ── Bake frame ──
  // ═══════════════════════════════════════════════════════

  /**
   * Bake a single frame: extract bbox/offset content at 1:1 pixel scale, centered in a square.
   * @param outSize — square output size (pixels). If 0, uses original frameWidth×frameHeight.
   */
  bakeFrame(sheetIdx: number, def: AnimFrameDef, outSize?: number): HTMLCanvasElement {
    const st = this.state;
    const fw = st.animDef.frameWidth;
    const fh = st.animDef.frameHeight;
    const scene = this.scene;
    const tex = scene.textures.get(st.selectedSheet);
    const frame = tex.get(sheetIdx);
    const source = frame.source.image as HTMLImageElement;
    const cd = frame.canvasData as { x: number; y: number; width: number; height: number };

    const sz = outSize || Math.max(fw, fh);
    const canvas = document.createElement('canvas');
    canvas.width = sz;
    canvas.height = sz;
    const ctx = canvas.getContext('2d')!;

    // Extract ONLY this frame's pixels (no neighbor bleed)
    const isoCanvas = document.createElement('canvas');
    isoCanvas.width = cd.width;
    isoCanvas.height = cd.height;
    const isoCtx = isoCanvas.getContext('2d')!;
    isoCtx.drawImage(source, cd.x, cd.y, cd.width, cd.height, 0, 0, cd.width, cd.height);

    const hasBbox = def.bboxW != null && def.bboxH != null && def.bboxW > 0 && def.bboxH > 0;

    if (hasBbox) {
      // Draw bbox content centered in square at 1:1 (no stretching)
      const bw = def.bboxW!;
      const bh = def.bboxH!;
      const dx = Math.round((sz - bw) / 2);
      const dy = Math.round((sz - bh) / 2);
      ctx.drawImage(source,
        cd.x + (def.bboxX ?? 0), cd.y + (def.bboxY ?? 0), bw, bh,
        dx, dy, bw, bh,
      );
    } else {
      // Legacy offset/scale — crop from isolated frame, centered in square
      const ox = def.offsetX ?? 0;
      const oy = def.offsetY ?? 0;
      const fs = def.scale ?? 1;
      const srcW = fw / fs;
      const srcH = fh / fs;
      const srcCx = cd.width / 2 - ox;
      const srcCy = cd.height / 2 - oy;
      const srcX = srcCx - srcW / 2;
      const srcY = srcCy - srcH / 2;
      const dw = Math.round(srcW * fs);
      const dh = Math.round(srcH * fs);
      const dx = Math.round((sz - dw) / 2);
      const dy = Math.round((sz - dh) / 2);
      ctx.drawImage(isoCanvas,
        srcX, srcY, srcW, srcH,
        dx, dy, dw, dh,
      );
    }

    return canvas;
  }

  // ═══════════════════════════════════════════════════════
  // ── Bake & Save ──
  // ═══════════════════════════════════════════════════════

  /**
   * Bake all frame adjustments into a new spritesheet PNG and trigger download.
   * After baking, resets all offsets to 0 and scales to 1.
   */
  bakeAndSave(): void {
    const st = this.state;
    if (!st.animDef || st.frameCount === 0) return;

    const scene = this.scene;
    const fw = st.animDef.frameWidth;
    const fh = st.animDef.frameHeight;
    const order = st.animDef.frameOrder;

    // Determine square frame size from bbox data (or fallback to max of fw/fh)
    let squareSize = Math.max(fw, fh);
    const bboxMaxes: number[] = [];
    for (const f of st.animDef.frames) {
      if (f.bboxW && f.bboxW > 0 && f.bboxH && f.bboxH > 0) {
        bboxMaxes.push(Math.max(f.bboxW, f.bboxH));
      }
    }
    if (bboxMaxes.length > 0) {
      squareSize = Math.max(...bboxMaxes) + 4; // +4px safety padding
    }

    // Grid: keep same column count as original, compute rows
    const tex = scene.textures.get(st.selectedSheet);
    const source = tex?.getSourceImage();
    const imgW = source?.width ?? fw;
    const cols = Math.max(1, Math.floor(imgW / fw));
    const rows = Math.ceil(st.frameCount / cols);

    const outCanvas = document.createElement('canvas');
    outCanvas.width = cols * squareSize;
    outCanvas.height = rows * squareSize;
    const outCtx = outCanvas.getContext('2d')!;

    for (let i = 0; i < st.frameCount; i++) {
      const sheetIdx = order[i] ?? i;
      const frameDef = st.animDef.frames[i];
      if (!frameDef) continue;

      const bakedCanvas = this.bakeFrame(sheetIdx, frameDef, squareSize);
      const col = i % cols;
      const row = Math.floor(i / cols);
      outCtx.drawImage(bakedCanvas, col * squareSize, row * squareSize);
    }

    const dataUrl = outCanvas.toDataURL('image/png');

    const origFile = st.animDef.sourceFile || `${st.selectedSheet}.png`;
    const baseName = origFile.replace(/\.png$/i, '').replace(/_\d+x\d+$/, '');
    const filename = `${baseName}_${cols}x${rows}.png`;

    fetch('/api/save-spritesheet', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, archiveFile: origFile, dataUrl }),
    }).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    }).then(({ savedAs, archivedAs }: { savedAs: string; archivedAs: string | null }) => {

      // Reset all per-frame adjustments (now baked into pixels)
      for (const f of st.animDef.frames) {
        f.offsetX = 0;
        f.offsetY = 0;
        f.scale = 1;
        f.bboxX = undefined as any;
        f.bboxY = undefined as any;
        f.bboxW = undefined as any;
        f.bboxH = undefined as any;
      }
      st.animDef.frameOrder = Array.from({ length: st.frameCount }, (_, i) => i);

      // Update frame dimensions to the baked square size
      st.animDef.frameWidth = squareSize;
      st.animDef.frameHeight = squareSize;

      const savedBasename = savedAs.includes('/') ? savedAs.substring(savedAs.lastIndexOf('/') + 1) : savedAs;
      const newKey = savedBasename.replace(/\.png$/i, '');
      const isNewKey = newKey !== st.selectedSheet;

      st.animDef.sourceFile = savedAs;
      if (isNewKey) {
        // Remove the old key from sheetKeys (original file was archived on disk)
        const oldIdx = st.sheetKeys.indexOf(st.selectedSheet);
        if (oldIdx !== -1) st.sheetKeys.splice(oldIdx, 1);
        // Remove old texture from Phaser (no longer exists under this name)
        if (scene.textures.exists(st.selectedSheet)) {
          scene.textures.remove(st.selectedSheet);
        }
        st.animDef.sheetKey = newKey;
        delete st.allDefs[st.selectedSheet];
        st.allDefs[newKey] = st.animDef;
      }
      this.cb.autoSave();

      const img = new Image();
      img.onload = () => {
        if (scene.textures.exists(newKey)) {
          scene.textures.remove(newKey);
        }
        scene.textures.addSpriteSheet(newKey, img, { frameWidth: squareSize, frameHeight: squareSize });

        if (isNewKey && !st.sheetKeys.includes(newKey)) {
          st.sheetKeys.push(newKey);
          st.sheetKeys.sort();
        }

        if (archivedAs) {
          const archivedKey = archivedAs.replace(/\.png$/i, '');
          if (!scene.textures.exists(archivedKey)) {
            const archivedImg = new Image();
            archivedImg.onload = () => {
              scene.textures.addSpriteSheet(archivedKey, archivedImg, { frameWidth: fw, frameHeight: fh }); // original frame size
              if (!st.sheetKeys.includes(archivedKey)) {
                st.sheetKeys.push(archivedKey);
                st.sheetKeys.sort();
              }
              this.cb.rebuildSidebarList();
            };
            const subDir = savedAs.includes('/') ? savedAs.substring(0, savedAs.lastIndexOf('/') + 1) : '';
            archivedImg.src = `${subDir}${archivedAs}`;
          }
        }

        st.selectedSheet = newKey;
        st.frameCount = scene.textures.get(newKey).frameTotal - 1;
        this.showFrame(st.selectedFrame >= 0 ? st.selectedFrame : 0);
        this.cb.rebuildSidebarList();
        this.cb.refreshTimeline();
        this.cb.refreshFrameProps();
        this.cb.refreshSheetProps();
      };
      img.src = dataUrl;

      const archivedMsg = archivedAs ? ` (original → ${archivedAs})` : '';
      const toast = scene.add.text(PREVIEW_X, PREVIEW_Y,
        `Baked → ${savedAs}${archivedMsg}`, {
          fontSize: '12px', fontFamily: 'monospace', fontStyle: 'bold', color: '#44ff88',
          backgroundColor: '#1a1a2e', padding: { x: 10, y: 6 },
        }).setOrigin(0.5).setDepth(30);
      scene.time.delayedCall(3000, () => toast.destroy());
    }).catch(err => {
      console.error('[BAKE] Save failed:', err);
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename.split('/').pop() || 'baked.png';
      a.click();

      const toast = scene.add.text(PREVIEW_X, PREVIEW_Y,
        `Save failed: ${err.message}\nDownloaded instead — put in public/${filename}`, {
          fontSize: '11px', fontFamily: 'monospace', color: '#ff8844',
          backgroundColor: '#1a1a2e', padding: { x: 10, y: 6 },
          wordWrap: { width: 300 },
        }).setOrigin(0.5).setDepth(30);
      scene.time.delayedCall(5000, () => toast.destroy());
    });
  }

  // ═══════════════════════════════════════════════════════
  // ── Exit / bake prompt ──
  // ═══════════════════════════════════════════════════════

  /** Check if any frame has non-default offset/scale (needs baking) */
  hasPendingAdjustments(): boolean {
    for (const def of Object.values(this.state.allDefs)) {
      for (const f of def.frames) {
        if (f.offsetX !== 0 || f.offsetY !== 0 || (f.scale !== undefined && f.scale !== 1)) {
          return true;
        }
      }
    }
    return false;
  }

  /** Exit with optional bake prompt */
  tryExit(): void {
    if (this.hasPendingAdjustments()) {
      this.showBakePrompt();
    } else {
      SoundManager.play('click');
      this.scene.scene.start('TitleScene');
    }
  }

  private showBakePrompt(): void {
    if (this.bakePromptContainer) return;

    const scene = this.scene;
    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;
    this.bakePromptContainer = scene.add.container(cx, cy).setDepth(50);

    const dim = scene.add.rectangle(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.6)
      .setInteractive();
    this.bakePromptContainer.add(dim);

    const panelW = 340;
    const panelH = 120;
    const bg = scene.add.graphics();
    fillTechRect(bg, -panelW / 2, -panelH / 2, panelW, panelH, 0x111122, 0.95);
    drawTechRect(bg, -panelW / 2, -panelH / 2, panelW, panelH, TECH.ACCENT, 0.8);
    this.bakePromptContainer.add(bg);

    this.bakePromptContainer.add(
      scene.add.text(0, -panelH / 2 + 18, 'Unsaved adjustments', {
        fontSize: '13px', fontFamily: 'monospace', fontStyle: 'bold', color: '#ffcc00',
      }).setOrigin(0.5)
    );
    this.bakePromptContainer.add(
      scene.add.text(0, -6, 'Bake before leaving?', {
        fontSize: '11px', fontFamily: 'monospace', color: '#aaaacc',
      }).setOrigin(0.5)
    );

    const closeBakePrompt = () => {
      if (this.bakePromptContainer) { this.bakePromptContainer.destroy(); this.bakePromptContainer = null; }
    };

    this.bakePromptContainer.add(createTechButton(scene, -100, panelH / 2 - 26, 'BAKE & EXIT', {
      onClick: () => {
        closeBakePrompt();
        this.bakeAndSave();
        scene.time.delayedCall(500, () => scene.scene.start('TitleScene'));
      },
      width: 110, height: 26, fontSize: '10px', color: TECH.SUCCESS,
    }));

    this.bakePromptContainer.add(createTechButton(scene, 20, panelH / 2 - 26, 'EXIT', {
      onClick: () => {
        closeBakePrompt();
        SoundManager.play('click');
        scene.scene.start('TitleScene');
      },
      width: 60, height: 26, fontSize: '10px', color: TECH.DANGER,
    }));

    this.bakePromptContainer.add(createTechButton(scene, 110, panelH / 2 - 26, 'CANCEL', {
      onClick: () => closeBakePrompt(),
      width: 70, height: 26, fontSize: '10px', color: TECH.DIM,
    }));
  }
}
